import {
    useCallback,
    useId,
    useImperativeHandle,
    useMemo,
    useState,
} from "react";
import { isAxiosError } from "axios";
import classNames from "classnames";
import type { ReactNode, Ref } from "react";
import { Field } from "@design-system/Field/Field";
import { HStack } from "@design-system/HStack/HStack";
import { Picker } from "@design-system/Picker/Picker.lib";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { PanelSection } from "@components/invoices/NativeInvoiceDrawer/PanelSection";
import panelStyles from "@components/invoices/NativeInvoiceDrawer/SidePanel.module.scss";
import { ConfirmDiscardChangesModal } from "@components/invoices/NativeInvoiceDrawer/ConfirmDiscardChangesModal";
import { useNativeInvoiceCloseGuard } from "@components/invoices/NativeInvoiceDrawer/useNativeInvoiceCloseGuard";
import {
    ReportSettingsTarget,
    WorkspaceReportSettingsScope,
} from "@common/helpers/report-settings/report-setting-candidates";
import {
    REPORT_SETTINGS_DEFAULT_REPORT_KEY,
    ReportSettingsConcreteReportKey,
} from "@common/types/reports/report-settings-report-key";
import { AccountingReportType } from "@common/types/reports/report-type";
import { ReportSettingsEditorHandle } from "../ReportSettingsEditorDrawer";
import styles from "../ReportSettings.module.scss";
import {
    REPORT_DEFAULT_LABELS,
    REPORT_DEFAULT_TOOLTIPS,
    REPORT_SETTINGS_PREVIEW_OPTIONS,
    REPORT_SETTINGS_REPORT_OPTIONS,
} from "../reportTypeOptions";
import { TemplateNames } from "../reportSettingsSourceCopy";
import { LayoutEditorForm, Section } from "./LayoutEditorForm";
import {
    ReportSettingsDraftSpot,
    UpdateReportSetting,
} from "./reportSettingsDrafts";
import { getPreviewValues, getSpotView } from "./reportSettingsDraftViews";
import { useDraftsFor } from "./useReportSettingsDrafts";

interface Props {
    drafts: ReturnType<typeof useDraftsFor>;
    target: ReportSettingsTarget;
    targetKey: string;
    scope: Partial<WorkspaceReportSettingsScope>;
    templateNames: TemplateNames;
    canEdit: boolean;
    entityPicker: ReactNode;
    entityName: string | null;
    firmName: string | null;
    firmLogoUrl: string | null;
    entityLogoUrl: string | null;
    isOpen: boolean;
    onRequestClose: () => void;
}

export const getUnsavedLabel = (isUnsaved: boolean): string | undefined =>
    isUnsaved ? "Unsaved" : undefined;

export const LayoutEditorShell = ({
    drafts,
    target,
    targetKey,
    scope,
    templateNames,
    canEdit,
    entityPicker,
    entityName,
    firmName,
    firmLogoUrl,
    entityLogoUrl,
    isOpen,
    onRequestClose,
    ref,
}: Props & { ref?: Ref<ReportSettingsEditorHandle> }) => {
    const [reportKey, setReportKey] = useState<
        ReportSettingsDraftSpot["reportKey"]
    >(REPORT_SETTINGS_DEFAULT_REPORT_KEY);
    const [previewReportKey, setPreviewReportKey] =
        useState<ReportSettingsConcreteReportKey>(
            AccountingReportType.PROFIT_LOSS,
        );
    const [openSection, setOpenSection] = useState<Section | null>("layout");
    const { toast: successToast, error: errorToast } = useToaster();
    const { saveAll, setField } = drafts;

    const spot = useMemo<ReportSettingsDraftSpot>(
        () => ({ target, reportKey }),
        [target, reportKey],
    );

    const save = useCallback(async (): Promise<boolean> => {
        try {
            const didWrite = await saveAll();
            if (didWrite) {
                successToast("Report settings saved");
            }
            return true;
        } catch (error) {
            if (!isAxiosError(error) || !error.response?.data?.message) {
                errorToast("Failed to save report settings");
            }
            return false;
        }
    }, [saveAll, successToast, errorToast]);

    const closeGuard = useNativeInvoiceCloseGuard({
        isEditable: true,
        isDirty: drafts.hasUnsavedEdits(),
        onClose: onRequestClose,
        onSave: save,
    });
    const { requestClose } = closeGuard;

    useImperativeHandle(ref, () => ({ requestClose }), [requestClose]);

    const updateField: UpdateReportSetting = useCallback(
        (key, value) => setField({ spot, key, value }),
        [setField, spot],
    );

    const reportDefaultLabel = REPORT_DEFAULT_LABELS[target.kind];
    const reportDefaultTooltip = REPORT_DEFAULT_TOOLTIPS[target.kind];
    const reportItems = REPORT_SETTINGS_REPORT_OPTIONS.filter(
        (option) => option.value !== AccountingReportType.SCHEDULES,
    ).map((option) => ({
        ...option,
        ...(option.value === REPORT_SETTINGS_DEFAULT_REPORT_KEY && {
            label: reportDefaultLabel,
            tooltip: reportDefaultTooltip,
        }),
        rightText: getUnsavedLabel(
            drafts.hasUnsavedEditsForSpot({ target, reportKey: option.value }),
        ),
    }));
    const reportSections = [
        {
            key: "default",
            items: reportItems.filter(
                (item) => item.value === REPORT_SETTINGS_DEFAULT_REPORT_KEY,
            ),
        },
        {
            key: "reports",
            items: reportItems.filter(
                (item) => item.value !== REPORT_SETTINGS_DEFAULT_REPORT_KEY,
            ),
        },
    ];

    const topControls = (
        <PanelSection hasCard>
            <div
                className={classNames(panelStyles.fieldGrid, {
                    [panelStyles.fieldGridWide]: !entityPicker,
                })}
            >
                {entityPicker}
                <Field label="Report" control="button">
                    <Tooltip
                        content={
                            reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY
                                ? reportDefaultTooltip
                                : undefined
                        }
                    >
                        <span className={styles.pickerTooltipTrigger}>
                            <Picker.Select
                                sections={reportSections}
                                searchable
                                value={reportKey}
                                onValueChange={(value) => {
                                    if (value !== null) {
                                        setReportKey(value);
                                    }
                                }}
                                renderValue={() =>
                                    reportKey ===
                                    REPORT_SETTINGS_DEFAULT_REPORT_KEY ? (
                                        <span>{reportDefaultLabel}</span>
                                    ) : undefined
                                }
                                trigger={
                                    <Picker.Button
                                        variant="primary"
                                        size="xl"
                                    />
                                }
                            />
                        </span>
                    </Tooltip>
                </Field>
            </div>
        </PanelSection>
    );

    const draftViewInput = {
        ...scope,
        rows: drafts.rows,
        drafts: drafts.drafts,
    };
    const isReportDefault = reportKey === REPORT_SETTINGS_DEFAULT_REPORT_KEY;
    const shownPreviewReportKey = isReportDefault
        ? previewReportKey
        : reportKey;
    const previewValues = getPreviewValues({
        ...draftViewInput,
        target,
        reportKey: shownPreviewReportKey,
    });

    const previewLabelId = useId();
    const previewPicker = isReportDefault ? (
        <HStack gap={4} align="center">
            <span id={previewLabelId} className={styles.previewPickerLabel}>
                Preview
            </span>
            <Picker.Select
                items={REPORT_SETTINGS_PREVIEW_OPTIONS}
                searchable
                value={previewReportKey}
                onValueChange={(value) => {
                    if (value !== null) {
                        setPreviewReportKey(value);
                    }
                }}
                trigger={
                    <Picker.Button
                        variant="primary"
                        size="md"
                        aria-labelledby={previewLabelId}
                    />
                }
            />
        </HStack>
    ) : null;

    return (
        <>
            <LayoutEditorForm
                key={`${targetKey}:${reportKey}`}
                spot={spot}
                templateNames={templateNames}
                view={getSpotView({ ...draftViewInput, spot })}
                previewLayout={previewValues.layout}
                previewNumberFormat={previewValues.numberFormat}
                previewReportKey={shownPreviewReportKey}
                previewPicker={previewPicker}
                canEdit={canEdit}
                isSaving={drafts.isSaving}
                isOpen={isOpen}
                topControls={topControls}
                openSection={openSection}
                onOpenSectionChange={setOpenSection}
                updateField={updateField}
                onSaveAndClose={closeGuard.saveAndClose}
                onCancel={requestClose}
                entityName={entityName}
                firmName={firmName}
                firmLogoUrl={firmLogoUrl}
                entityLogoUrl={entityLogoUrl}
            />
            <ConfirmDiscardChangesModal
                show={closeGuard.isDiscardModalOpen}
                isSaving={drafts.isSaving}
                onSave={closeGuard.saveAndClose}
                onDiscard={onRequestClose}
                onCancel={closeGuard.cancelClose}
            />
        </>
    );
};
